/**
 * Data freshness utilities for OctoTrack
 *
 * Provides utilities to calculate and display data freshness indicators.
 * Helps users understand when shipment data was last updated and if it's stale.
 *
 * @module lib/data-freshness
 */

import { differenceInHours, differenceInMinutes } from "date-fns"
import { parseDate, type DateInput, formatRelative } from "./date-utils"

// Re-export DateInput for consumers
export type { DateInput } from "./date-utils"

// ============================================================================
// Types
// ============================================================================

/**
 * Data freshness level indicators
 */
export type FreshnessLevel = "fresh" | "recent" | "stale"

/**
 * Data freshness status with metadata
 */
export interface FreshnessStatus {
  level: FreshnessLevel
  hoursAgo: number | null
  label: string
  description: string
  isStale: boolean
}

/**
 * Freshness threshold configuration
 */
export interface FreshnessThresholds {
  freshHours: number // Data is "fresh" if updated within this many hours
  recentHours: number // Data is "recent" if updated within this many hours
}

// ============================================================================
// Constants
// ============================================================================

/**
 * Default freshness thresholds
 * - Fresh: < 1 hour
 * - Recent: 1-24 hours
 * - Stale: > 24 hours
 */
export const DEFAULT_FRESHNESS_THRESHOLDS: FreshnessThresholds = {
  freshHours: 1,
  recentHours: 24,
}

/**
 * Default stale data threshold in hours
 */
export const DEFAULT_STALE_THRESHOLD_HOURS = 24

// ============================================================================
// Core Freshness Functions
// ============================================================================

/**
 * Check if data is stale based on a timestamp
 *
 * @param timestamp - The last updated timestamp (Date, ISO string, or null)
 * @param thresholdHours - Hours threshold to consider data stale (default: 24)
 * @returns true if data is stale (older than threshold), false otherwise
 *
 * @example
 * isStaleData("2025-01-22T10:00:00Z", 24) // true if >24 hours ago
 * isStaleData("2025-01-23T14:00:00Z", 24) // false if <24 hours ago
 * isStaleData(null, 24) // false (no timestamp = not stale)
 */
export function isStaleData(
  timestamp: DateInput,
  thresholdHours: number = DEFAULT_STALE_THRESHOLD_HOURS
): boolean {
  const date = parseDate(timestamp)
  if (!date) return false

  try {
    const hoursAgo = differenceInHours(new Date(), date)
    return hoursAgo > thresholdHours
  } catch {
    return false
  }
}

/**
 * Calculate the freshness level of data based on timestamp
 *
 * @param timestamp - The last updated timestamp
 * @param thresholds - Custom freshness thresholds (optional)
 * @returns Freshness level: 'fresh', 'recent', or 'stale'
 *
 * @example
 * calculateFreshnessLevel("2025-01-23T14:30:00Z") // "fresh" if <1 hour ago
 * calculateFreshnessLevel("2025-01-23T10:00:00Z") // "recent" if 1-24 hours ago
 * calculateFreshnessLevel("2025-01-22T10:00:00Z") // "stale" if >24 hours ago
 */
export function calculateFreshnessLevel(
  timestamp: DateInput,
  thresholds: FreshnessThresholds = DEFAULT_FRESHNESS_THRESHOLDS
): FreshnessLevel {
  const date = parseDate(timestamp)
  if (!date) return "stale"

  try {
    const hoursAgo = differenceInHours(new Date(), date)

    if (hoursAgo < thresholds.freshHours) {
      return "fresh"
    } else if (hoursAgo < thresholds.recentHours) {
      return "recent"
    } else {
      return "stale"
    }
  } catch {
    return "stale"
  }
}

/**
 * Get comprehensive data freshness status with metadata
 *
 * @param timestamp - The last updated timestamp
 * @param thresholds - Custom freshness thresholds (optional)
 * @returns Complete freshness status object
 *
 * @example
 * getDataFreshnessStatus("2025-01-23T14:30:00Z")
 * // {
 * //   level: "fresh",
 * //   hoursAgo: 0,
 * //   label: "Just updated",
 * //   description: "Data was updated less than 1 hour ago",
 * //   isStale: false
 * // }
 */
export function getDataFreshnessStatus(
  timestamp: DateInput,
  thresholds: FreshnessThresholds = DEFAULT_FRESHNESS_THRESHOLDS
): FreshnessStatus {
  const date = parseDate(timestamp)

  if (!date) {
    return {
      level: "stale",
      hoursAgo: null,
      label: "Never updated",
      description: "No update timestamp available",
      isStale: true,
    }
  }

  try {
    const hoursAgo = differenceInHours(new Date(), date)
    const minutesAgo = differenceInMinutes(new Date(), date)
    const level = calculateFreshnessLevel(timestamp, thresholds)
    const isStale = level === "stale"

    let label: string
    let description: string

    switch (level) {
      case "fresh":
        if (minutesAgo < 1) {
          label = "Just updated"
          description = "Data was updated less than a minute ago"
        } else if (minutesAgo < 60) {
          label = `${minutesAgo} min ago`
          description = `Data was updated ${minutesAgo} minute${minutesAgo === 1 ? "" : "s"} ago`
        } else {
          label = "Recently updated"
          description = "Data was updated less than 1 hour ago"
        }
        break

      case "recent":
        if (hoursAgo === 1) {
          label = "1 hour ago"
          description = "Data was updated 1 hour ago"
        } else {
          label = `${hoursAgo} hours ago`
          description = `Data was updated ${hoursAgo} hours ago`
        }
        break

      case "stale":
        if (hoursAgo < 48) {
          label = `${hoursAgo} hours ago`
          description = `Data may be outdated. Last updated ${hoursAgo} hours ago.`
        } else {
          const daysAgo = Math.floor(hoursAgo / 24)
          label = `${daysAgo} day${daysAgo === 1 ? "" : "s"} ago`
          description = `Data may be outdated. Last updated ${daysAgo} day${daysAgo === 1 ? "" : "s"} ago.`
        }
        break

      default:
        label = formatRelative(timestamp, { fallback: "Unknown" })
        description = "Data freshness unknown"
    }

    return {
      level,
      hoursAgo,
      label,
      description,
      isStale,
    }
  } catch {
    return {
      level: "stale",
      hoursAgo: null,
      label: "Unknown",
      description: "Unable to determine data freshness",
      isStale: true,
    }
  }
}

// ============================================================================
// Shorthand Helper Functions
// ============================================================================

/**
 * Check if data is fresh (updated within the last hour)
 *
 * @param timestamp - The last updated timestamp
 * @returns true if data is fresh, false otherwise
 */
export function isFreshData(timestamp: DateInput): boolean {
  return calculateFreshnessLevel(timestamp) === "fresh"
}

/**
 * Check if data is recent (updated within 1-24 hours)
 *
 * @param timestamp - The last updated timestamp
 * @returns true if data is recent, false otherwise
 */
export function isRecentData(timestamp: DateInput): boolean {
  return calculateFreshnessLevel(timestamp) === "recent"
}

/**
 * Get hours since last update
 *
 * @param timestamp - The last updated timestamp
 * @returns Number of hours since update, or null if invalid timestamp
 *
 * @example
 * getHoursSinceUpdate("2025-01-23T10:00:00Z") // 4 (if current time is 14:00)
 */
export function getHoursSinceUpdate(timestamp: DateInput): number | null {
  const date = parseDate(timestamp)
  if (!date) return null

  try {
    return differenceInHours(new Date(), date)
  } catch {
    return null
  }
}

/**
 * Get minutes since last update
 *
 * @param timestamp - The last updated timestamp
 * @returns Number of minutes since update, or null if invalid timestamp
 *
 * @example
 * getMinutesSinceUpdate("2025-01-23T14:50:00Z") // 10 (if current time is 15:00)
 */
export function getMinutesSinceUpdate(timestamp: DateInput): number | null {
  const date = parseDate(timestamp)
  if (!date) return null

  try {
    return differenceInMinutes(new Date(), date)
  } catch {
    return null
  }
}

// ============================================================================
// Display Utilities
// ============================================================================

/**
 * Get a simple freshness label for display
 *
 * @param timestamp - The last updated timestamp
 * @returns Human-readable freshness label
 *
 * @example
 * getFreshnessLabel("2025-01-23T14:55:00Z") // "5 min ago"
 * getFreshnessLabel("2025-01-23T10:00:00Z") // "4 hours ago"
 * getFreshnessLabel("2025-01-22T10:00:00Z") // "1 day ago"
 */
export function getFreshnessLabel(timestamp: DateInput): string {
  const status = getDataFreshnessStatus(timestamp)
  return status.label
}

/**
 * Get a freshness description for tooltips
 *
 * @param timestamp - The last updated timestamp
 * @returns Detailed freshness description
 *
 * @example
 * getFreshnessDescription("2025-01-23T14:55:00Z")
 * // "Data was updated 5 minutes ago"
 */
export function getFreshnessDescription(timestamp: DateInput): string {
  const status = getDataFreshnessStatus(timestamp)
  return status.description
}

/**
 * Get color class for freshness level (for UI styling)
 *
 * @param level - Freshness level
 * @returns Tailwind color class suffix (e.g., "green", "yellow", "red")
 *
 * @example
 * getFreshnessColor("fresh") // "green"
 * getFreshnessColor("recent") // "yellow"
 * getFreshnessColor("stale") // "red"
 */
export function getFreshnessColor(level: FreshnessLevel): string {
  switch (level) {
    case "fresh":
      return "green"
    case "recent":
      return "yellow"
    case "stale":
      return "red"
    default:
      return "gray"
  }
}
